import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Seo, breadcrumbLd, faqLd } from "../lib/seo";
import { MaskedLines, Reveal, Chapter, ParallaxImg } from "../lib/motion";
import { BreakCard } from "../components/BreakCard";

export default function HubPage({ slug }) {
  const [hub, setHub] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setHub(null);
    setMissing(false);
    api.get(`/hubs/${slug}`).then((r) => setHub(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <main className="container-tpb py-24 text-center" data-testid="hub-not-found">
        <h1 className="font-serif text-4xl">Page not found</h1>
        <Link to="/breaks" className="btn-primary mt-6">Browse all breaks</Link>
      </main>
    );
  }
  if (!hub) return <main className="container-tpb py-24"><p className="text-ink-muted">Loading…</p></main>;

  const jsonLd = [
    breadcrumbLd([{ name: "Home", path: "/" }, { name: hub.title, path: `/${hub.slug}` }]),
    ...(hub.faqs?.length ? [faqLd(hub.faqs)] : []),
  ];

  return (
    <main data-testid={`hub-page-${hub.slug}`}>
      <Seo title={hub.seo_title} description={hub.seo_description} path={`/${hub.slug}`} jsonLd={jsonLd} />

      <section className="relative overflow-hidden bg-primary">
        <ParallaxImg src={hub.image} alt={hub.title} className="absolute inset-0 h-full w-full" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/25" />
        <div className="container-tpb relative flex min-h-[56vh] flex-col justify-end pb-14 pt-32 sm:min-h-[64vh]">
          <p className="overline !text-sand/90" data-testid="hub-overline">{hub.overline}</p>
          <h1 className="mt-4 max-w-4xl font-serif text-4xl font-medium leading-[1.03] tracking-tight text-sand sm:text-6xl" style={{ textShadow: "0 2px 22px rgba(0,0,0,0.45)" }} data-testid="hub-title">
            <MaskedLines immediate delay={0.3} lines={[hub.title]} />
          </h1>
        </div>
      </section>

      <section className="container-tpb py-16 sm:py-24">
        <Reveal>
          <p className="max-w-3xl text-lg leading-relaxed text-ink sm:text-xl" data-testid="hub-intro">{hub.intro}</p>
        </Reveal>

        <div className="mt-16 grid gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-4" data-testid="hub-breaks-grid">
          {hub.breaks.map((b, i) => <BreakCard key={b.slug} brk={b} index={i} />)}
        </div>

        <Reveal>
          <aside className="mt-20 rounded-lg border border-sandline bg-sand-dark p-8 sm:p-10" data-testid="hub-value-explainer">
            <h2 className="font-serif text-2xl font-semibold text-primary sm:text-3xl">Package vs DIY: the honest maths</h2>
            <p className="mt-3 max-w-3xl leading-relaxed text-ink-muted">
              Price a hotel, breakfast and three experiences separately and the total — and the admin — climbs fast. A Perfect Break bundles them for one price for two people, with every experience hand-picked. You spend your break on the break, not on the planning.
            </p>
          </aside>
        </Reveal>

        {hub.faqs?.length > 0 && (
          <section className="mt-20" data-testid="hub-faqs">
            <Chapter num="№ 02" overline="Good to know" title="Questions, answered" />
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {hub.faqs.map((f, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="h-full rounded-lg border border-sandline bg-white p-7" data-testid={`hub-faq-${i}`}>
                    <h3 className="font-sans text-base font-semibold text-ink">{f.q}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink-muted">{f.a}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        <nav className="mt-20 border-t border-sandline pt-10" aria-label="Related pages" data-testid="hub-related-links">
          <p className="overline">You might also like</p>
          <div className="mt-5 flex flex-wrap gap-3">
            {[
              { to: "/short-breaks", label: "UK short breaks" },
              { to: "/couples-breaks", label: "Couples breaks" },
              { to: "/weekend-breaks", label: "Weekend breaks" },
              { to: "/experience-breaks", label: "Experience breaks" },
              { to: "/heart-of-england", label: "The Heart of England" },
              { to: "/compare", label: "How we compare" },
            ].filter((l) => l.to !== `/${hub.slug}`).map((l) => (
              <Link key={l.to} to={l.to} className="btn-ghost !py-2" data-testid={`hub-related-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>{l.label}</Link>
            ))}
          </div>
        </nav>
      </section>
    </main>
  );
}
